import * as React from "react";
import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Box, Typography, Button, FormControl, FormLabel, Input, Link } from "@mui/joy";
import { PasswordStrengthInput } from "../components/PasswordStrengthInput";
import { validators } from "../utils/validators";

const ResetPassword: React.FC = () => {
  const [searchParams] = useSearchParams();
  const token = useMemo(() => (searchParams.get("token") || "").trim(), [searchParams]);

  const [validating, setValidating] = useState<boolean>(true);
  const [tokenValid, setTokenValid] = useState<boolean>(false);
  const [tokenError, setTokenError] = useState<string | null>(null);

  const [password, setPassword] = useState<string>("");
  const [confirmPassword, setConfirmPassword] = useState<string>("");
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [cooldownSeconds, setCooldownSeconds] = useState<number>(0);

  useEffect(() => {
    document.title = "Reset Password | Iskcon Kanpur";
  }, []);

  useEffect(() => {
    if (cooldownSeconds <= 0) {
      return;
    }
    const timer = setTimeout(() => setCooldownSeconds((prev) => Math.max(0, prev - 1)), 1000);
    return () => clearTimeout(timer);
  }, [cooldownSeconds]);

  useEffect(() => {
    const validateToken = async () => {
      if (!token) {
        setValidating(false);
        setTokenValid(false);
        setTokenError("Reset link is missing or invalid.");
        return;
      }

      setValidating(true);
      setTokenError(null);

      try {
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_API_URL}/auth/reset-password/validate?token=${encodeURIComponent(token)}`,
          {
            method: "GET",
            credentials: "include",
          }
        );

        if (!response.ok) {
          setTokenValid(false);
          setTokenError("This reset link is invalid or expired.");
          return;
        }

        setTokenValid(true);
      } catch {
        setTokenValid(false);
        setTokenError("Unable to validate reset link right now.");
      } finally {
        setValidating(false);
      }
    };

    validateToken();
  }, [token]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    setMessage(null);
    setLoading(true);

    try {
      const passwordValidation = validators.password(password);
      if (!passwordValidation.valid) {
        throw new Error(passwordValidation.error || "Password is invalid");
      }

      const matchValidation = validators.passwordMatch(password, confirmPassword);
      if (!matchValidation.valid) {
        throw new Error(matchValidation.error || "Passwords do not match");
      }

      const response = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/auth/reset-password/confirm`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({
          token,
          newPassword: password,
        }),
      });

      if (!response.ok) {
        let retryAfter = 0;
        let backendError = "Unable to reset password. The link may be expired.";
        try {
          const data = await response.json();
          if (typeof data?.retry_after_seconds === "number") {
            retryAfter = Math.max(0, Math.floor(data.retry_after_seconds));
          }
          if (typeof data?.error === "string" && data.error.trim()) {
            backendError = data.error;
          }
        } catch {
          // no-op: use fallback message
        }

        if (retryAfter > 0) {
          setCooldownSeconds(retryAfter);
          throw new Error(`Too many attempts. Try again in ${retryAfter}s.`);
        }
        throw new Error(backendError);
      }

      setMessage("Password has been reset successfully. You can now sign in.");
      setPassword("");
      setConfirmPassword("");
      setTokenValid(false);
    } catch (err: any) {
      setError(err?.message || "Unable to reset password right now.");
    } finally {
      setLoading(false);
    }
  };

  if (validating) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          height: "100vh",
          textAlign: "center",
        }}
      >
        <Typography level="h2" fontSize="1.5rem" sx={{ mb: 1 }}>
          Validating reset link
        </Typography>
        <Typography level="body-sm">Please wait...</Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "100vh",
        textAlign: "center",
        px: 2,
      }}
    >
      <Typography level="h2" fontSize="1.5rem" sx={{ mb: 2 }}>
        Reset your password
      </Typography>

      {tokenError && (
        <Typography level="body-sm" sx={{ color: "danger.500", mb: 2 }}>
          {tokenError}
        </Typography>
      )}

      {message && (
        <Typography level="body-sm" sx={{ color: "success.500", mb: 2 }}>
          {message}
        </Typography>
      )}

      {tokenValid ? (
        <Box component="form" onSubmit={handleSubmit} sx={{ width: "100%", maxWidth: 360 }}>
          <PasswordStrengthInput
            password={password}
            onChange={setPassword}
            label="New Password"
            placeholder="Enter new password"
            disabled={loading}
          />

          <FormControl sx={{ mt: 2 }}>
            <FormLabel>Confirm New Password</FormLabel>
            <Input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Re-enter new password"
              disabled={loading}
            />
          </FormControl>

          {error && (
            <Typography level="body-sm" sx={{ color: "danger.500", mt: 1 }}>
              {error}
            </Typography>
          )}
          {cooldownSeconds > 0 && (
            <Typography level="body-sm" sx={{ color: "warning.500", mt: 1 }}>
              Please wait {cooldownSeconds}s before trying again.
            </Typography>
          )}

          <Button type="submit" fullWidth sx={{ mt: 3 }} loading={loading} disabled={loading || cooldownSeconds > 0}>
            {cooldownSeconds > 0 ? `Try again in ${cooldownSeconds}s` : "Set new password"}
          </Button>
        </Box>
      ) : null}

      <Typography level="body-sm" sx={{ mt: 3 }}>
        <Link href="/forget-password">Request a new reset link</Link>
      </Typography>
      <Typography level="body-sm" sx={{ mt: 1 }}>
        <Link href="/signin">Back to sign in</Link>
      </Typography>
    </Box>
  );
};

export default ResetPassword;
